/**
 * Forma do símbolo dos focos: uma chama estilizada — corpo com a ponta
 * inclinada para a direita, uma "língua" menor saindo para a esquerda e uma
 * chama interna mais clara —, para não confundir com o ponto circular das
 * sedes municipais (nem com uma gota).
 *
 * Uma única descrição geométrica gera as três saídas, que ficam iguais:
 * - GLSL (SDF analítica, glPoints.ts) — "gotas" (cápsula desigual com ponta
 *   arredondada) com a ponta curvada; a silhueta é a união (min) delas; nada
 *   de textura;
 * - polígonos (Path2D do atlas do canvas 2D, layer.ts);
 * - caminho SVG (legenda do painel).
 *
 * Unidades: múltiplos do raio base do foco (`style.size × scale`), origem na
 * posição do foco, y para CIMA.
 */

export interface DropShape {
  /** Centro do bulbo (x). */
  cx: number;
  /** Centro do bulbo (y). */
  cy: number;
  /** Raio do bulbo. */
  r1: number;
  /** Raio (arredondamento) da ponta. */
  r2: number;
  /** Distância do centro do bulbo à ponta. */
  h: number;
  /** Deslocamento horizontal da ponta (curva quadrática ao longo da altura). */
  bend: number;
}

/** Silhueta da chama = união destas gotas (cor do foco, com contorno escuro): corpo e língua lateral. */
export const FLAME_PARTS: readonly DropShape[] = [
  { cx: 0.04, cy: -0.4, r1: 0.84, r2: 0.06, h: 1.82, bend: 0.46 },
  { cx: -0.32, cy: -0.58, r1: 0.45, r2: 0.05, h: 1.12, bend: -0.52 },
];
/** Chama interna (cor do foco clareada). */
export const FLAME_INNER: DropShape = { cx: 0.04, cy: -0.66, r1: 0.44, r2: 0.04, h: 0.95, bend: 0.16 };
/** Quanto a chama interna clareia (mistura com branco). */
export const FLAME_INNER_WHITE = 0.6;
/** Raio mínimo (px do dispositivo) para desenhar a chama interna — abaixo disso vira borrão. */
export const FLAME_INNER_MIN_PX = 2.5;

/** Deslocamento x da forma na altura y (mesma curva no shader e no polígono). */
function bendAt(s: DropShape, y: number): number {
  const q = Math.min(1, Math.max(0, (y - s.cy) / s.h));
  return s.bend * q * q;
}

/**
 * Contorno da gota como polígono (unidades do raio, y para cima): arco do
 * bulbo, reta tangente, arco da ponta, reta tangente — e o deslocamento.
 */
export function dropPolygon(s: DropShape, arcSegments = 28, tipSegments = 6): Array<[number, number]> {
  const b = (s.r1 - s.r2) / s.h;
  const th = Math.asin(b); // ângulo do ponto de tangência acima da horizontal
  const pts: Array<[number, number]> = [];
  const push = (x0: number, y: number) => pts.push([s.cx + x0 + bendAt(s, y), y]);
  // bulbo: de th (direita) passando por baixo até π − th (esquerda)
  for (let i = 0; i <= arcSegments; i++) {
    const a = th - ((Math.PI + 2 * th) * i) / arcSegments;
    push(s.r1 * Math.cos(a), s.cy + s.r1 * Math.sin(a));
  }
  // ponta: de π − th (esquerda) por cima até th (direita)
  const ty = s.cy + s.h;
  for (let i = 0; i <= tipSegments; i++) {
    const a = Math.PI - th - ((Math.PI - 2 * th) * i) / tipSegments;
    push(s.r2 * Math.cos(a), ty + s.r2 * Math.sin(a));
  }
  return pts;
}

/**
 * Distância com sinal (aprox.) até a gota — espelho em TS da função GLSL,
 * usada nos testes. Negativa dentro.
 */
export function dropSd(s: DropShape, x: number, y: number): number {
  const py = y - s.cy;
  const px = Math.abs(x - s.cx - bendAt(s, y));
  const b = (s.r1 - s.r2) / s.h;
  const a = Math.sqrt(1 - b * b);
  const k = -b * px + a * py;
  if (k < 0) return Math.hypot(px, py) - s.r1;
  if (k > a * s.h) return Math.hypot(px, py - s.h) - s.r2;
  return a * px + b * py - s.r1;
}

/** Distância com sinal até a silhueta (união das partes). */
export const flameSd = (x: number, y: number) => Math.min(...FLAME_PARTS.map((s) => dropSd(s, x, y)));

const PART_POLYS = FLAME_PARTS.map((s) => dropPolygon(s));
const OUTER_POLY = PART_POLYS.flat();
const INNER_POLY = dropPolygon(FLAME_INNER);

/**
 * Maior afastamento da chama em relação à posição do foco (unidades do
 * raio), para dimensionar `gl_PointSize` e os sprites.
 */
export const FLAME_EXTENT = Math.max(...OUTER_POLY.map(([x, y]) => Math.max(Math.abs(x), Math.abs(y))));

/** Caixa da chama (unidades do raio, y para cima), para o clique. */
export const FLAME_BOX = {
  left: Math.min(...OUTER_POLY.map((p) => p[0])),
  right: Math.max(...OUTER_POLY.map((p) => p[0])),
  bottom: Math.min(...OUTER_POLY.map((p) => p[1])),
  top: Math.max(...OUTER_POLY.map((p) => p[1])),
};

/** Caminho SVG (y para BAIXO, como no canvas/SVG) de um polígono em unidades do raio. */
function svgPath(poly: Array<[number, number]>): string {
  const f = (n: number) => String(Math.round(n * 1000) / 1000);
  return poly.map(([x, y], i) => `${i ? 'L' : 'M'}${f(x)} ${f(-y)}`).join('') + 'Z';
}

/**
 * Caminhos SVG (y para baixo): `outer` é a silhueta (subcaminhos com a mesma
 * orientação — preenchimento nonzero = união), `parts` cada gota, `inner` a
 * chama interna.
 */
export const FLAME_SVG = {
  outer: PART_POLYS.map(svgPath).join(''),
  parts: PART_POLYS.map(svgPath),
  inner: svgPath(INNER_POLY),
};

export interface FlamePaths {
  outer: Path2D;
  parts: Path2D[];
  inner: Path2D;
}

/** Path2D (y para baixo, unidades do raio) — criado sob demanda (só no navegador). */
let paths: FlamePaths | null = null;
export function flamePaths(): FlamePaths {
  paths ??= {
    outer: new Path2D(FLAME_SVG.outer),
    parts: FLAME_SVG.parts.map((d) => new Path2D(d)),
    inner: new Path2D(FLAME_SVG.inner),
  };
  return paths;
}

/**
 * Contorno escuro só na borda da silhueta (sem as divisas internas entre as
 * partes), igual ao `abs(sd)` do shader: cada parte é traçada com recorte
 * que exclui o interior das outras. `ctx` já em unidades do raio.
 */
export function strokeFlame(ctx: CanvasRenderingContext2D, p: FlamePaths): void {
  const E = 4; // recorte bem maior que a chama
  p.parts.forEach((part, i) => {
    ctx.save();
    const clip = new Path2D();
    clip.rect(-E, -E, 2 * E, 2 * E);
    p.parts.forEach((other, j) => {
      if (j !== i) clip.addPath(other);
    });
    ctx.clip(clip, 'evenodd');
    ctx.stroke(part);
    ctx.restore();
  });
}

/** Número GLSL (sempre com ponto decimal). */
const g = (n: number) => (Number.isInteger(n) ? `${n}.0` : String(Math.round(n * 1e6) / 1e6));

function dropGlsl(name: string, s: DropShape): string {
  const b = (s.r1 - s.r2) / s.h;
  const a = Math.sqrt(1 - b * b);
  return `
float ${name}(vec2 p) {
  p -= vec2(${g(s.cx)}, ${g(s.cy)});
  float q = clamp(p.y / ${g(s.h)}, 0.0, 1.0);
  p.x = abs(p.x - ${g(s.bend)} * q * q);
  float k = dot(p, vec2(${g(-b)}, ${g(a)}));
  if (k < 0.0) return length(p) - ${g(s.r1)};
  if (k > ${g(a * s.h)}) return length(p - vec2(0.0, ${g(s.h)})) - ${g(s.r2)};
  return dot(p, vec2(${g(a)}, ${g(b)})) - ${g(s.r1)};
}`;
}

/** Funções GLSL `flameOuter(p)` e `flameInner(p)` (p em unidades do raio, y para cima). */
export const FLAME_GLSL =
  FLAME_PARTS.map((s, i) => dropGlsl(`flamePart${i}`, s)).join('') +
  dropGlsl('flameInner', FLAME_INNER) +
  `
float flameOuter(vec2 p) {
  return ${FLAME_PARTS.map((_, i) => `flamePart${i}(p)`).reduce((acc, c) => `min(${acc}, ${c})`)};
}`;
